import React, { useState, useEffect } from 'react';
import './Auth.css';

const COGNITO_CONFIG = {
  userPoolId: 'us-east-1_luAAhsNng',
  clientId: '1e6pobo7vbdttoptbsl38tqf1r',
  region: 'us-east-1'
};

const USER_ACCOUNT_API = 'https://563t3yrmw5d42zyd7gw6eeeun40htlid.lambda-url.us-east-1.on.aws';

function Auth({ onAuthSuccess, onBack }) {
  const [isLogin, setIsLogin] = useState(true);
  const [referralCode, setReferralCode] = useState(null);
  const [isForgotPassword, setIsForgotPassword] = useState(false);
  const [isResetPassword, setIsResetPassword] = useState(false);

  // Capture referral code from URL on mount
  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const ref = params.get('ref');
    if (ref) {
      setReferralCode(ref);
      localStorage.setItem('referralCode', ref);
      window.history.replaceState({}, '', window.location.pathname);
    } else {
      const stored = localStorage.getItem('referralCode');
      if (stored) setReferralCode(stored);
    }
  }, []);
  
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [name, setName] = useState('');
  const [verificationCode, setVerificationCode] = useState('');
  const [needsVerification, setNeedsVerification] = useState(false);
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setLoading(true);

    try {
      if (isForgotPassword) {
        await handleForgotPassword();
      } else if (isResetPassword) {
        await handleResetPassword();
      } else if (needsVerification) {
        await handleVerification();
      } else if (isLogin) {
        await handleLogin();
      } else {
        await handleSignup();
      }
    } catch (err) {
      setError(err.message || 'Authentication failed');
    } finally {
      setLoading(false);
    }
  };

  const handleForgotPassword = async () => {
    const response = await fetch('https://cognito-idp.us-east-1.amazonaws.com/', {
      method: 'POST',
      headers: {
        'Content-Type': 'application/x-amz-json-1.1',
        'X-Amz-Target': 'AWSCognitoIdentityProviderService.ForgotPassword'
      },
      body: JSON.stringify({
        ClientId: COGNITO_CONFIG.clientId,
        Username: email
      })
    });

    const data = await response.json();
    if (!response.ok) {
      throw new Error(data.message || 'Failed to send reset code');
    }

    setIsForgotPassword(false);
    setIsResetPassword(true);
    alert('Password reset code sent to your email!');
  };

  const handleResetPassword = async () => {
    const response = await fetch('https://cognito-idp.us-east-1.amazonaws.com/', {
      method: 'POST',
      headers: {
        'Content-Type': 'application/x-amz-json-1.1',
        'X-Amz-Target': 'AWSCognitoIdentityProviderService.ConfirmForgotPassword'
      },
      body: JSON.stringify({
        ClientId: COGNITO_CONFIG.clientId,
        Username: email,
        ConfirmationCode: verificationCode,
        Password: newPassword
      })
    });

    const data = await response.json();
    if (!response.ok) {
      throw new Error(data.message || 'Failed to reset password');
    }

    alert('Password reset successful! You can now login.');
    setIsResetPassword(false);
    setIsLogin(true);
    setVerificationCode('');
    setNewPassword('');
  };

  const handleLogin = async () => {
    const response = await fetch('https://cognito-idp.us-east-1.amazonaws.com/', {
      method: 'POST',
      headers: {
        'Content-Type': 'application/x-amz-json-1.1',
        'X-Amz-Target': 'AWSCognitoIdentityProviderService.InitiateAuth'
      },
      body: JSON.stringify({
        AuthFlow: 'USER_PASSWORD_AUTH',
        ClientId: COGNITO_CONFIG.clientId,
        AuthParameters: {
          USERNAME: email,
          PASSWORD: password
        }
      })
    });

    const data = await response.json();
    
    if (!response.ok) {
      if (data.__type === 'UserNotConfirmedException') {
        setError('Please verify your email first. Check your inbox for the verification code.');
        setNeedsVerification(true);
        return;
      }
      throw new Error(data.message || 'Login failed');
    }

    const { IdToken, AccessToken, RefreshToken } = data.AuthenticationResult;
    localStorage.setItem('idToken', IdToken);
    localStorage.setItem('accessToken', AccessToken);
    localStorage.setItem('refreshToken', RefreshToken);
    
    // Create/update profile with referral code if present
    const storedRef = localStorage.getItem('referralCode');
    if (storedRef) {
      try {
        await fetch(`${USER_ACCOUNT_API}/api/account`, {
          method: 'POST',
          headers: { 'Authorization': IdToken, 'Content-Type': 'application/json' },
          body: JSON.stringify({ email, referral_code: storedRef })
        });
        localStorage.removeItem('referralCode'); // Clear after use
      } catch (e) { console.error('Failed to save referral:', e); }
    }
    
    onAuthSuccess({ email, token: IdToken });
  };

  const handleSignup = async () => {
    const response = await fetch('https://cognito-idp.us-east-1.amazonaws.com/', {
      method: 'POST',
      headers: {
        'Content-Type': 'application/x-amz-json-1.1',
        'X-Amz-Target': 'AWSCognitoIdentityProviderService.SignUp'
      },
      body: JSON.stringify({
        ClientId: COGNITO_CONFIG.clientId,
        Username: email,
        Password: password,
        UserAttributes: [
          { Name: 'email', Value: email },
          { Name: 'name', Value: name }
        ]
      })
    });

    const data = await response.json();
    
    if (!response.ok) {
      throw new Error(data.message || 'Signup failed');
    }

    setNeedsVerification(true);
    setError('');
    alert('Signup successful! Please check your email for a verification code.');
  };

  const handleVerification = async () => {
    const response = await fetch('https://cognito-idp.us-east-1.amazonaws.com/', {
      method: 'POST',
      headers: {
        'Content-Type': 'application/x-amz-json-1.1',
        'X-Amz-Target': 'AWSCognitoIdentityProviderService.ConfirmSignUp'
      },
      body: JSON.stringify({
        ClientId: COGNITO_CONFIG.clientId,
        Username: email,
        ConfirmationCode: verificationCode
      })
    });

    const data = await response.json();
    
    if (!response.ok) {
      throw new Error(data.message || 'Verification failed');
    }

    alert('Email verified! You can now login.');
    setNeedsVerification(false);
    setIsLogin(true);
    setVerificationCode('');
  };

  const resendCode = async () => {
    setLoading(true);
    try {
      const response = await fetch('https://cognito-idp.us-east-1.amazonaws.com/', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/x-amz-json-1.1',
          'X-Amz-Target': 'AWSCognitoIdentityProviderService.ResendConfirmationCode'
        },
        body: JSON.stringify({
          ClientId: COGNITO_CONFIG.clientId,
          Username: email
        })
      });

      if (response.ok) {
        alert('Verification code resent! Check your email.');
      }
    } catch (err) {
      setError('Failed to resend code');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="auth-container">
      <div className="auth-box">
        {onBack && (
          <button type="button" className="back-btn" onClick={onBack}>← Back</button>
        )}
        <h2>🎯 BetPilot</h2>
        {referralCode && !isLogin && (
          <div className="referral-badge">🎁 Referred by a friend!</div>
        )}
        <h3>
          {isForgotPassword ? 'Forgot Password' : 
           isResetPassword ? 'Reset Password' :
           needsVerification ? 'Verify Email' : 
           (isLogin ? 'Login' : 'Sign Up')}
        </h3>
        <form onSubmit={handleSubmit}>
          {isForgotPassword ? (
            <>
              <p>Enter your email to receive a password reset code</p>
              <input
                type="email"
                placeholder="Email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                required
              />
            </>
          ) : isResetPassword ? (
            <>
              <p>Enter the code sent to {email} and your new password</p>
              <input
                type="text"
                placeholder="Reset Code"
                value={verificationCode}
                onChange={(e) => setVerificationCode(e.target.value)}
                required
              />
              <input
                type="password"
                placeholder="New Password"
                value={newPassword}
                onChange={(e) => setNewPassword(e.target.value)}
                required
              />
            </>
          ) : needsVerification ? (
            <>
              <p>Enter the verification code sent to {email}</p>
              <input
                type="text"
                placeholder="Verification Code"
                value={verificationCode}
                onChange={(e) => setVerificationCode(e.target.value)}
                required
              />
              <button type="button" onClick={resendCode} className="resend-btn">
                Resend Code
              </button>
            </>
          ) : (
            <>
              {!isLogin && (
                <input
                  type="text"
                  placeholder="Name"
                  value={name}
                  onChange={(e) => setName(e.target.value)}
                  required
                />
              )}
              <input
                type="email"
                placeholder="Email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                required
              />
              <input
                type="password"
                placeholder="Password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                required
              />
            </>
          )}
          {error && <div className="error">{error}</div>}
          <button type="submit" disabled={loading}>
            {loading ? 'Loading...' : 
             isForgotPassword ? 'Send Reset Code' :
             isResetPassword ? 'Reset Password' :
             needsVerification ? 'Verify' : 
             (isLogin ? 'Login' : 'Sign Up')}
          </button>
        </form>
        {isLogin && !needsVerification && !isForgotPassword && !isResetPassword && (
          <p className="forgot-password">
            <span onClick={() => setIsForgotPassword(true)}>Forgot Password?</span>
          </p>
        )}
        {(isForgotPassword || isResetPassword) && (
          <p className="toggle-auth">
            <span onClick={() => { setIsForgotPassword(false); setIsResetPassword(false); setIsLogin(true); }}>
              Back to Login
            </span>
          </p>
        )}
        {!needsVerification && !isForgotPassword && !isResetPassword && (
          <p className="toggle-auth">
            {isLogin ? "Don't have an account? " : "Already have an account? "}
            <span onClick={() => setIsLogin(!isLogin)}>
              {isLogin ? 'Sign Up' : 'Login'}
            </span>
          </p>
        )}
        {needsVerification && (
          <p className="toggle-auth">
            <span onClick={() => { setNeedsVerification(false); setIsLogin(true); }}>
              Back to Login
            </span>
          </p>
        )}
      </div>
    </div>
  );
}

export default Auth;
